import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, FileQuestion, Download } from 'lucide-react';
import { api } from '../lib/api';

const cache = new Map();
const kind = (f) => (f.type?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(f.name || '') ? 'image' : f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '') ? 'pdf' : 'other');

export const FilePreview = ({ evidenceId, file, children, className }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [state, setState] = useState({ status: 'idle', url: null, error: null });
  const timer = useRef(null);
  const k = kind(file);

  useEffect(() => {
    if (!open || k === 'other' || file.seed) return undefined;
    const key = `${evidenceId}/${file.id}`;
    if (cache.has(key)) { setState({ status: 'ready', url: cache.get(key) }); return undefined; }
    let alive = true;
    setState({ status: 'loading', url: null });
    api.get(`/evidence/${evidenceId}/files/${file.id}/preview`, { responseType: 'blob' })
      .then((res) => { const url = URL.createObjectURL(res.data); cache.set(key, url); if (alive) setState({ status: 'ready', url }); })
      .catch((e) => alive && setState({ status: 'error', url: null, error: e?.response?.status === 404 ? 'File binary is not stored for this demo item.' : 'Preview could not be loaded.' }));
    return () => { alive = false; };
  }, [open, evidenceId, file.id, file.seed, k]);

  const show = (e) => { const r = e.currentTarget.getBoundingClientRect(); setPos({ x: Math.min(r.right + 12, window.innerWidth - 380), y: Math.max(12, Math.min(r.top, window.innerHeight - 340)) }); clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), 220); };
  const hide = () => { clearTimeout(timer.current); setOpen(false); };

  return (
    <div className={className} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} data-testid={`file-hover-${file.id}`}>
      {children}
      {open && createPortal(
        <div data-testid={`file-preview-${file.id}`} className="fixed z-[60] w-[360px] rounded-xl border border-line bg-white shadow-lift overflow-hidden pointer-events-none live-fade" style={{ left: pos.x, top: pos.y }}>
          <div className="px-3 py-2 border-b border-line flex items-center justify-between gap-2"><span className="font-mono text-[11px] text-ink-800 truncate">{file.name}</span><span className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold shrink-0">{k === 'other' ? (file.name?.split('.').pop() || 'file') : k}</span></div>
          <div className="h-[280px] bg-paper-100 flex items-center justify-center">
            {file.seed || k === 'other' ? (
              <Unavailable text={file.seed ? 'Demo file — no binary is stored, so there is nothing to preview.' : `No inline preview for .${file.name?.split('.').pop()} files.`} />
            ) : state.status === 'ready' ? (
              k === 'image' ? <img src={state.url} alt={file.name} className="max-h-full max-w-full object-contain" /> : <iframe title={file.name} src={`${state.url}#toolbar=0&navpanes=0&view=FitH`} className="w-full h-full bg-white" />
            ) : state.status === 'error' ? <Unavailable text={state.error} /> : <Loader2 className="h-5 w-5 animate-spin text-ink-400" />}
          </div>
        </div>, document.body)}
    </div>
  );
};

const Unavailable = ({ text }) => (
  <div className="px-6 text-center" data-testid="file-preview-unavailable"><FileQuestion className="h-6 w-6 mx-auto text-ink-300" /><div className="mt-2 text-sm font-medium text-ink-700">Preview not available</div><p className="mt-1 text-xs text-ink-500">{text}</p><p className="mt-2 text-xs text-ink-600 font-medium inline-flex items-center gap-1"><Download className="h-3 w-3" />Click the file to download and view it.</p></div>
);
